/**
 * src/gm/sandbox/smoke-probe.ts — iframe 探针冒烟台架（设计稿 §9.6/§9.7 全继承）。
 *
 * 机器能做的最低限度试玩：sandbox="allow-scripts"（禁 same-origin/forms/popups），
 * 注入探针采集 window.onerror / console.error / canvas 检测，hook rAF 只统计
 * 游戏自身注册的回调（探针自身绝不用 rAF 心跳，否则任何白屏页都会"自证"PASS）。
 * 3 秒后模拟一次键盘 + 点击输入观察画面/状态变化，汇总成冒烟报告。
 * 报告 JSON 化后注入评审 prompt 作为证据（降级试玩协议）。
 */

import { SMOKE_TIMEOUT_MS } from '../platform/config'

export interface SmokeReport {
  errors: string[]
  consoleErrors: string[]
  /** 探针是否至少回报过一次（false = 脚本被 CSP/沙箱整体拦截，无法判定） */
  probeAlive: boolean
  /** 游戏自身 rAF 循环活跃（探针心跳不计入） */
  rafActive: boolean
  canvasPresent: boolean
  hasDomGame: boolean
  responsive: boolean
  /** null = 探针未存活（环境阻断），不计入 FAIL —— 防误报（事故尸检 R3 修复） */
  verdict: 'PASS' | 'FAIL' | null
}

/**
 * 报告汇总 → 判定（纯函数，Node 侧可单测）。
 * 误报防护：探针本身也是内联脚本，若被 CSP（script-src 缺 'unsafe-inline'）
 * 或沙箱整体拦截，永远收不到任何回报——此时 errors 为空、画布/rAF 全假，
 * 与"游戏白屏"表现完全相同。区分关键就是 probeAlive：
 *   探针未存活 → verdict=null（环境阻断，不算游戏的事故）；
 *   探针存活但游戏无画面/无循环/报错 → FAIL（游戏真的死了）。
 */
export function computeVerdict(report: Omit<SmokeReport, 'verdict'>): 'PASS' | 'FAIL' | null {
  if (!report.probeAlive) return null
  const hasGameSurface = report.canvasPresent || report.hasDomGame
  const lively = report.rafActive || report.responsive
  return report.errors.length === 0 && hasGameSurface && lively ? 'PASS' : 'FAIL'
}

/**
 * E2 死因归类（copy-deck §九终稿，措辞逐字）：冒烟 FAIL 的英文原文/堆栈不上浮 toast，
 * 按错误特征归四类人话摘要（≤20 字）；未命中一律走兜底——禁止截断原文冒充摘要。
 * 原文由调用方完整进 console.error。归类优先级：禁区 > 语法 > 无循环 > 兜底
 * （SecurityError 常伴随 rafActive:false，禁区特征优先于无循环）。
 */
export function summarizeSmokeFailure(report: SmokeReport): string {
  const text = report.errors.join('\n')
  if (/securityerror|localstorage|sessionstorage|indexeddb|document\.cookie/i.test(text)) {
    return '开机即报错：碰到了沙箱禁区'
  }
  if (/syntaxerror|脚本未能加载/i.test(text)) {
    return '没能开工：代码本身有错误'
  }
  if (!report.rafActive) {
    return '点不亮：画面没有跑起来的迹象'
  }
  return '开机即报错（细节见控制台）'
}

/**
 * 模拟输入事件清单（B4 尸检修复：只模拟桌面浏览器真实存在的事件形态——
 * 桌面冒烟没有触屏，裸 new Event('touchstart') 缺 changedTouches，
 * 会把"监听 touch 且未守卫"的游戏误杀成 FAIL；两盘狗粮两次作案 @577/@461）。
 * 键盘事件（Enter/方向键/空格，KeyboardEvent 形态真实）在 PROBE 内另列。
 */
export const SIMULATED_INPUT_EVENTS = ['click', 'pointerdown', 'mousedown'] as const

/**
 * 探针脚本：注入 <head> 最前，postMessage 心跳回报。
 * 纪律：探针的一切定时都用 setTimeout/setInterval，requestAnimationFrame 只做
 * hook 统计——state.raf 的每一次计数都来自游戏代码自己注册的回调。
 */
const PROBE = `<script>(function(){
  var state = { errors: [], consoleErrors: [], raf: 0, canvas: false, dom: false, changed: false };
  window.onerror = function(msg, src, line){ state.errors.push(String(msg) + (line ? '@' + line : '')); };
  var origErr = console.error;
  console.error = function(){ state.consoleErrors.push(Array.from(arguments).join(' ')); origErr.apply(console, arguments); };
  var origRaf = window.requestAnimationFrame;
  if (origRaf) {
    window.requestAnimationFrame = function(cb){
      return origRaf.call(window, function(ts){
        state.raf++;
        try { cb(ts); } catch(e){ state.errors.push('rAF 回调异常: ' + String((e && e.message) || e)); }
      });
    };
  }
  function report(final){
    var cv = document.querySelector('canvas');
    state.canvas = !!(cv && cv.width > 0 && cv.height > 0);
    state.dom = !!(document.body && document.body.children.length > 0);
    try { parent.postMessage({ __tp3probe: true, final: !!final, state: state }, '*'); } catch(e){}
  }
  setTimeout(function(){
    // 模拟"开始一局"的输入：Enter / 方向键 / 空格 / 点击各一发
    var before = document.body ? document.body.innerHTML.length : 0;
    ['Enter','ArrowUp','ArrowLeft',' '].forEach(function(k){
      ['keydown','keyup'].forEach(function(t){
        document.dispatchEvent(new KeyboardEvent(t, { key: k, bubbles: true }));
        document.body && document.body.dispatchEvent(new KeyboardEvent(t, { key: k, bubbles: true }));
      });
    });
    ${JSON.stringify(SIMULATED_INPUT_EVENTS)}.forEach(function(t){
      try { document.body && document.body.dispatchEvent(new Event(t, { bubbles: true })); } catch(e){}
    });
    setTimeout(function(){
      var after = document.body ? document.body.innerHTML.length : 0;
      state.changed = before !== after || state.raf > 30;
      report(true);
    }, 1200);
  }, 3000);
  setInterval(function(){ report(false); }, 800);
})();</script>`

function instrument(html: string): string {
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (m) => m + PROBE)
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (m) => m + '<head>' + PROBE + '</head>')
  return PROBE + html
}

/**
 * 冒烟测试：timeoutMs 内收集探针心跳 → 汇总报告。
 * 判定（PASS 必要条件，全部满足）：
 *   ⓪ 探针存活（否则 verdict=null——环境阻断，不误判 FAIL）；
 *   ① 无未捕获异常；
 *   ② 有游戏画面：canvas 存在且尺寸 > 0，或存在 DOM 游戏元素；
 *   ③ 游戏是活的：游戏自身 rAF 循环活跃，或对模拟输入有有效响应。
 *      探针自身心跳不计入任何判定指标。
 * FAIL = 白屏 / 打开即报错 / 无循环迹象。
 */
export function smokeTest(html: string, { timeoutMs = SMOKE_TIMEOUT_MS } = {}): Promise<SmokeReport> {
  return new Promise((resolve) => {
    const report: SmokeReport = {
      errors: [],
      consoleErrors: [],
      probeAlive: false,
      rafActive: false,
      canvasPresent: false,
      hasDomGame: false,
      responsive: false,
      verdict: null,
    }
    const iframe = document.createElement('iframe')
    iframe.setAttribute('sandbox', 'allow-scripts')
    iframe.style.cssText = 'position:absolute;width:480px;height:320px;left:-9999px;top:0;opacity:0;pointer-events:none;'

    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      window.removeEventListener('message', onMessage)
      clearTimeout(timer)
      iframe.remove()
      report.verdict = computeVerdict(report)
      resolve(report)
    }

    interface ProbeState {
      errors?: string[]
      consoleErrors?: string[]
      raf?: number
      canvas?: boolean
      dom?: boolean
      changed?: boolean
    }
    const onMessage = (e: MessageEvent) => {
      // 来源校验：只收本 iframe 沙箱发出的报告（防页面内其他脚本伪造 PASS）
      if (e.source !== iframe.contentWindow) return
      const data = e.data as { __tp3probe?: boolean; final?: boolean; state?: ProbeState } | null
      if (!data?.__tp3probe || !data.state) return
      report.probeAlive = true // 探针跑起来了——之后的 FAIL 才是游戏自己的死法
      const s = data.state
      report.errors = [...new Set([...report.errors, ...(s.errors ?? [])])]
      report.consoleErrors = [...new Set([...report.consoleErrors, ...(s.consoleErrors ?? [])])]
      report.rafActive = report.rafActive || (s.raf ?? 0) > 10
      report.canvasPresent = report.canvasPresent || !!s.canvas
      report.hasDomGame = report.hasDomGame || !!s.dom
      report.responsive = report.responsive || !!s.changed
      if (data.final) finish()
    }

    const timer = setTimeout(finish, timeoutMs)
    window.addEventListener('message', onMessage)
    document.body.appendChild(iframe)
    iframe.srcdoc = instrument(html)
  })
}
